import React, { useState, useRef } from 'react'
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Paper
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'

// Parse variantPrices which may be an object or a double-encoded JSON string.
// Prices are kept as the TEXT in the box, not numbers: a number state turned an
// emptied field straight back into 0, so the 0 could not be deleted, only
// selected and typed over. They are parsed only when handed to the parent.
function buildVariants(vp) {
  let parsed = vp
  if (typeof vp === 'string') {
    try { parsed = JSON.parse(vp) } catch (e) { parsed = {} }
  }
  if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
    return Object.entries(parsed).map(([name, price]) => ({
      name,
      price: price == null ? '' : String(price)
    }))
  }
  return [{ name: '', price: '' }]
}

const DynamicPricingTable = ({ variantPrices, onChange, label }) => {
  const { t } = useTranslation()
  const [variants, setVariants] = useState(() => buildVariants(variantPrices))

  // Re-initialize when a genuinely different item is loaded from outside
  const lastPropRef = useRef(variantPrices)
  if (variantPrices !== lastPropRef.current) {
    const propStr = JSON.stringify(variantPrices)
    const lastStr = JSON.stringify(lastPropRef.current)
    if (propStr !== lastStr) {
      const currentObj = {}
      variants.forEach(v => { if (v.name?.trim()) currentObj[v.name.trim()] = parseFloat(v.price) || 0 })
      if (propStr !== JSON.stringify(currentObj)) {
        setVariants(buildVariants(variantPrices))
      }
    }
    lastPropRef.current = variantPrices
  }

  const notifyParent = (variantArray) => {
    const pricesObj = {}
    variantArray.forEach(v => {
      if (v.name && v.name.trim()) {
        pricesObj[v.name.trim()] = parseFloat(v.price) || 0
      }
    })
    lastPropRef.current = pricesObj
    onChange && onChange(pricesObj)
  }

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = variants.map((v, i) => {
      if (i !== index) return v
      return { ...v, [field]: value }
    })
    setVariants(updatedVariants)
    notifyParent(updatedVariants)
  }

  const addVariant = () => {
    setVariants(prev => [...prev, { name: '', price: '' }])
  }

  const deleteVariant = (index) => {
    if (variants.length === 1) return
    const updatedVariants = variants.filter((_, i) => i !== index)
    setVariants(updatedVariants)
    notifyParent(updatedVariants)
  }

  return (
    <Box sx={{ mt: 2 }}>
      {label && (
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#344767' }}>
          {label}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {variants.map((variant, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              label={idx === 0 ? t('Name') : undefined}
              placeholder={t('e.g., Small')}
              value={variant.name}
              onChange={(e) => handleVariantChange(idx, 'name', e.target.value)}
              sx={{ flex: 2, minWidth: 0 }}
              InputProps={{ sx: { color: '#344767' } }}
            />
            <TextField
              size="small"
              type="number"
              label={idx === 0 ? t('Price') : undefined}
              placeholder="0.00"
              value={variant.price}
              onChange={(e) => handleVariantChange(idx, 'price', e.target.value)}
              inputProps={{ step: 0.01, min: 0 }}
              sx={{ flex: 1, minWidth: 0 }}
              InputProps={{ sx: { color: '#344767' } }}
            />
            <IconButton
              size="small"
              onClick={() => deleteVariant(idx)}
              disabled={variants.length === 1}
              sx={{ color: '#7b809a' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addVariant}
          size="small"
          sx={{ mt: 1, color: '#344767' }}
        >
          {t('Add Variant')}
        </Button>

        <Box sx={{ mt: 2, p: 1, bgcolor: '#e8f4fd', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#344767' }}>
            {t('Example')}: Small: $8.99, Medium: $12.99, Large: $16.99
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default DynamicPricingTable
